import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { WORKS } from "@/data/works";
import { useT } from "@/i18n/LanguageContext";

function buildSections(t) {
  return [
    { kind: "section", label: t("cmd.section.home"), to: "/", hint: "↩" },
    { kind: "section", label: t("cmd.section.selected_works"), to: "/", state: { scrollTo: "works" }, hint: "↩" },
    { kind: "section", label: t("cmd.section.philosophy"), to: "/", state: { scrollTo: "philosophy" }, hint: "↩" },
    { kind: "section", label: t("cmd.section.words"), to: "/", state: { scrollTo: "words" }, hint: "↩" },
    { kind: "section", label: t("cmd.section.archive"), to: "/works", hint: "↩" },
    { kind: "section", label: t("cmd.section.exhibitions"), to: "/exhibitions", hint: "↩" },
    { kind: "section", label: t("cmd.section.about"), to: "/about", hint: "↩" },
    { kind: "section", label: t("cmd.section.contact"), to: "/contact", hint: "↩" },
  ];
}

const WORK_ITEMS = WORKS.map((w) => ({
  kind: "work",
  label: w.title,
  meta: `${w.year}`,
  to: `/works/${w.slug}`,
  search: `${w.title} ${w.year} ${w.slug}`.toLowerCase(),
}));

export default function CommandPalette() {
  const { t } = useT();
  const SECTIONS = useMemo(() => buildSections(t), [t]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Global hotkey: Cmd/Ctrl+K or "/" (when no input is focused)
  useEffect(() => {
    const onKey = (e) => {
      const isTyping =
        document.activeElement &&
        ["INPUT", "TEXTAREA"].includes(document.activeElement.tagName);

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "/" && !isTyping && !open) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sections = SECTIONS.filter((s) =>
      needle === "" ? true : s.label.toLowerCase().includes(needle)
    );
    const works = WORK_ITEMS.filter((w) =>
      needle === "" ? true : w.search.includes(needle)
    ).slice(0, 18);
    return [...sections, ...works];
  }, [q, SECTIONS]);

  useEffect(() => {
    setIdx(0);
  }, [q]);

  const choose = (item) => {
    setOpen(false);
    if (item.state) navigate(item.to, { state: item.state });
    else navigate(item.to);
    window.scrollTo(0, 0);
  };

  const onListKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[idx];
      if (item) choose(item);
    }
  };

  // Keep selected item visible
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-pal-idx="${idx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [idx]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="command-palette"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[9700] bg-ink/70 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
        >
          <motion.div
            initial={{ y: -12, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-bone text-ink border border-ink/20 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)]"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-ink/15">
              <span className="font-mono text-[10px] tracking-[0.35em] uppercase text-ink/55">
                ↪ {t("cmd.placeholder").split(/…|\.\.\./)[0].split(/[,…]/)[0].split("…")[0]}
              </span>
              <input
                ref={inputRef}
                data-testid="command-palette-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onListKey}
                placeholder={t("cmd.placeholder")}
                className="flex-1 bg-transparent outline-none font-serif text-xl placeholder:text-ink/35"
              />
              <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-ink/40">
                ESC
              </span>
            </div>

            <div ref={listRef} className="max-h-[55vh] overflow-y-auto no-scrollbar">
              {results.length === 0 && (
                <div className="px-5 py-8 font-serif italic text-ink/50">
                  {t("cmd.empty")}
                </div>
              )}
              {results.map((item, i) => (
                <button
                  key={`${item.kind}-${item.to}-${item.label}-${i}`}
                  data-pal-idx={i}
                  data-testid={`palette-item-${i}`}
                  onMouseEnter={() => setIdx(i)}
                  onClick={() => choose(item)}
                  className={`w-full text-left px-5 py-3 flex items-baseline gap-4 border-b border-ink/8 transition-colors ${
                    i === idx ? "bg-ink text-bone" : "hover:bg-ink/5"
                  }`}
                >
                  <span
                    className={`font-mono text-[9px] tracking-[0.3em] uppercase ${
                      i === idx ? "text-bone/55" : "text-ink/40"
                    }`}
                  >
                    {item.kind === "work" ? t("cmd.works") : t("cmd.sections")}
                  </span>
                  <span className="font-serif text-lg flex-1 truncate">
                    {item.label}
                  </span>
                  {item.meta && (
                    <span
                      className={`font-mono text-[9px] tracking-[0.3em] uppercase ${
                        i === idx ? "text-bone/55" : "text-ink/40"
                      }`}
                    >
                      {item.meta}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="px-5 py-3 border-t border-ink/15 flex items-center justify-between font-mono text-[9px] tracking-[0.3em] uppercase text-ink/45">
              <span>↑ ↓ navigate · ↩ open</span>
              <span>⌘K · /</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
